import { z } from 'zod';
import { Api } from '@automapa/svelte-automapa-template';
import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';

// Schema dla żądania (tworzenie/edycja reguły)

// [{"enabled":true,"address_id":-26,"name_part":null,"start_of_time_window":"02:00","end_of_time_window":"05:30","id":1,"revision":"61f20353fc173bd18d20afd3028d7dec","address":{"id":-26,"geom":{"type":"Point","crs":{"type":"name","properties":{"name":"EPSG:4326"}},"coordinates":[20.8873587,52.22756]},"country":"PL","province":"mazowieckie","district":"Warszawa","community":"Warszawa","city":"Warszawa","quarter":null,"pcode":"01-320","street":"Szeligowska","house":"32","street_for_sorting":"Szeligowska","house_as_number":"32","entry_points":null,"revision":null}}]

export const AddressSchemaRequest = z.object({
    geom: z.object({
        type: z.string().regex(/^Point$/),
        crs: z.object({
            type: z.string().regex(/^name$/),
            properties: z.object({
                name: z.string().regex(/^EPSG:4326$/),
            }),
        }),
        coordinates: z.array(z.number()),
    }),
    country: z.string().catch(''),
    province: z.string().catch(''),
    district: z.string().catch(''),
    community: z.string().catch(''),
    city: z.string().catch(''),
    quarter: z.string().nullable().catch(null),
    pcode: z.string().catch(''),
    street: z.string().catch(''),
    house: z.string().catch(''),
    street_for_sorting: z.string().catch(''),
    house_as_number: z.string().catch(''),
    entry_points: z.any().nullable().catch(null),
    revision: z.string().nullable().catch(null),
});
export type AddressRequestType = z.infer<typeof AddressSchemaRequest>;

export const AddressSchemaResponse = AddressSchemaRequest.extend({
    id: z.number(),
});
export type AddressResponseType = z.infer<typeof AddressSchemaResponse>;

export const JobModifyTimeWindowRuleRequestSchema = z.object({
    enabled: z.boolean().catch(true),
    address_id: z.number().nullable().catch(null),
    name_part: z.string().nullable().catch(null),
    start_of_time_window: z
        .string()
        .regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/)
        .nullable()
        .catch(null),
    end_of_time_window: z
        .string()
        .regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/)
        .nullable()
        .catch(null),
});

export type JobModifyTimeWindowRuleRequestType = z.infer<
    typeof JobModifyTimeWindowRuleRequestSchema
>;

// Schema dla odpowiedzi (zawiera dodatkowe pola z backendu)
export const JobModifyTimeWindowRuleResponseSchema = JobModifyTimeWindowRuleRequestSchema.merge(
    z.object({
        id: z.number(),
        address: AddressSchemaResponse.nullable().catch(null),
    })
);

export type JobModifyTimeWindowRuleResponseType = z.infer<
    typeof JobModifyTimeWindowRuleResponseSchema
>;

// Schema dla licznika
export const CountSchema = z.object({
    count: z.coerce.number().catch(0),
});
export type CountType = z.infer<typeof CountSchema>;

// {"geocoded_address":{"x":21.3537855,"y":52.22146,"quality":8,"countryCodes":{"iso2":"PL","iso3":"POL","targeo":"PL"},"country":"Polska","province":"mazowieckie","district":"mi\u0144ski","community":"Halin\u00f3w","city":"Hipolit\u00f3w","quarter":"","postcode":"05-074","street":"Kolejowa","house":"12","mapLink":"","house_geoId":""}}
export const GeocodedAddressSchema = z.object({
    geocoded_address: z.object({
        x: z.number(),
        y: z.number(),
        quality: z.number(),
        countryCodes: z.object({
            iso2: z.string(),
            iso3: z.string(),
            targeo: z.string(),
        }),
        country: z.string(),
        province: z.string(),
        district: z.string(),
        community: z.string(),
        city: z.string(),
        quarter: z.string(),
        postcode: z.string(),
        street: z.string(),
        house: z.string(),
        mapLink: z.string(),
        house_geoId: z.string(),
    }),
});
export type GeocodedAddressType = z.infer<typeof GeocodedAddressSchema>;

// Schema dla odpowiedzi importu CSV
export const CsvImportResponseSchema = z.object({
    success_count: z.number(),
    error_count: z.number(),
    errors: z.array(
        z.object({
            row: z.number(),
            message: z.string(),
        })
    ),
});
export type CsvImportResponseType = z.infer<typeof CsvImportResponseSchema>;

// Klasa API
export class JobModifyTimeWindowRuleApi {
    /**
     * Pobiera wszystkie reguły
     */
    public static async getAll(api: Api): Promise<JobModifyTimeWindowRuleResponseType[]> {
        let filter = new ApiFilterBuilder(null);
        filter.sortBy('id', 'desc');
        filter.addRelation('address');
        return api.getData<undefined, JobModifyTimeWindowRuleResponseType[]>(
            'get',
            '/job_modify_time_window_rules' + filter.getUri(),
            undefined,
            z.undefined(),
            z.array(JobModifyTimeWindowRuleResponseSchema)
        );
    }

    /**
     * Pobiera pojedynczą regułę po ID
     */
    public static async getOne(
        api: Api,
        ruleId: number
    ): Promise<JobModifyTimeWindowRuleResponseType> {
        return api.getData<undefined, JobModifyTimeWindowRuleResponseType>(
            'get',
            `/job_modify_time_window_rule/${ruleId}`,
            undefined,
            z.undefined(),
            JobModifyTimeWindowRuleResponseSchema
        );
    }

    /**
     * Tworzy nową regułę
     */
    public static async createOne(
        api: Api,
        rule: JobModifyTimeWindowRuleRequestType
    ): Promise<JobModifyTimeWindowRuleResponseType> {
        return api.getData<JobModifyTimeWindowRuleRequestType, JobModifyTimeWindowRuleResponseType>(
            'post',
            `/job_modify_time_window_rule`,
            rule,
            JobModifyTimeWindowRuleRequestSchema,
            JobModifyTimeWindowRuleResponseSchema
        );
    }

    /**
     * Aktualizuje istniejącą regułę
     */
    public static async updateOne(
        api: Api,
        ruleId: number,
        rule: JobModifyTimeWindowRuleRequestType
    ): Promise<JobModifyTimeWindowRuleResponseType> {
        return api.getData<JobModifyTimeWindowRuleRequestType, JobModifyTimeWindowRuleResponseType>(
            'put',
            `/job_modify_time_window_rule/${ruleId}`,
            rule,
            JobModifyTimeWindowRuleRequestSchema,
            JobModifyTimeWindowRuleResponseSchema
        );
    }

    /**
     * Usuwa regułę (soft delete)
     */
    public static async removeOne(api: Api, ruleId: number): Promise<any> {
        return api.delete(`/job_modify_time_window_rule/${ruleId}`);
    }

    /**
     * Usuwa wszystkie reguły klienta (soft delete)
     */
    public static async removeAll(api: Api): Promise<{ success: boolean }> {
        return api.getData<any, { success: boolean }>(
            'post',
            '/job_modify_time_window_rule/removeAll',
            {},
            z.any(),
            z.object({ success: z.boolean() })
        );
    }

    /**
     * Pobiera liczbę reguł
     */
    public static async getCount(api: Api): Promise<CountType> {
        return api.getData<undefined, CountType>(
            'get',
            '/job_modify_time_window_rules/count',
            undefined,
            z.undefined(),
            CountSchema
        );
    }

    /**
     * Filtruje reguły
     */
    public static async filter(
        api: Api,
        filter: ApiFilterBuilder
    ): Promise<JobModifyTimeWindowRuleResponseType[]> {
        filter.sortBy('id', 'desc');
        return api.getData<undefined, JobModifyTimeWindowRuleResponseType[]>(
            'get',
            '/job_modify_time_window_rules' + filter.getUri(),
            undefined,
            z.undefined(),
            z.array(JobModifyTimeWindowRuleResponseSchema)
        );
    }

    // applyTimeWindowRules - zwraca numery jobów, które zostały zmodyfikowane
    public static async applyTimeWindowRules(
        api: Api
    ): Promise<{ updated_job_ids: number[]; used_rules: number[] }> {
        return api.getData<any, { updated_job_ids: number[]; used_rules: number[] }>(
            'post',
            '/job/apply_time_window_rules',
            {},
            z.any(),
            z.object({ updated_job_ids: z.array(z.number()), used_rules: z.array(z.number()) })
        );
    }

    public static async setEnabled(
        api: Api,
        ruleId: number,
        enabled: boolean
    ): Promise<JobModifyTimeWindowRuleResponseType> {
        return api.patch<JobModifyTimeWindowRuleResponseType>(
            `/job_modify_time_window_rule/${ruleId}`,
            'replace',
            '/enabled',
            enabled,
            JobModifyTimeWindowRuleResponseSchema
        );
    }

    /**
     * Eksportuje reguły do pliku CSV zgodnego z formatem importu.
     */
    public static async exportToCsv(api: Api): Promise<{ csv_content: string }> {
        return api.getData<any, { csv_content: string }>(
            'post',
            '/job_modify_time_window_rule/export_to_csv',
            {},
            z.any(),
            z.object({ csv_content: z.string() })
        );
    }

    /**
     * Importuje reguły z pliku CSV w jednym requeście.
     * Backend wykonuje geokodowanie, tworzenie adresów i reguł.
     */
    public static async importFromCsv(
        api: Api,
        csvContent: string
    ): Promise<CsvImportResponseType> {
        try {
            return await api.getData<{ csv_content: string }, CsvImportResponseType>(
                'post',
                '/job_modify_time_window_rule/import_from_csv',
                { csv_content: csvContent },
                z.object({ csv_content: z.string() }),
                CsvImportResponseSchema
            );
        } catch (err) {
            if (err === 'csv_too_many_rows') {
                const rowCount = csvContent.split('\n').filter(l => l.trim().length > 0).length - 1;
                throw new Error(
                    `Została przekroczona maksymalna liczba reguł w pliku (maksimum to 100, w pliku ${rowCount})`
                );
            }
            throw err;
        }
    }

    /*
        public static function geocodeByAddress(Operator $dbOperator): array
    {
        try {
            $city = $dbOperator->getData()['city'] ?? throw new \Exception('city is required');
            $street = $dbOperator->getData()['street'] ?? throw new \Exception('street is required');
            $house = $dbOperator->getData()['house'] ?? throw new \Exception('house is required');
            $pcode = $dbOperator->getData()['pcode'] ?? throw new \Exception('pcode is required');
     */
    public static async geocodeByAddress(
        api: Api,
        city: string,
        street: string,
        house: string,
        pcode: string
    ): Promise<GeocodedAddressType> {
        return api.getData<any, GeocodedAddressType>(
            'post',
            '/job/geocode_by_address',
            { city, street, house, pcode },
            z.any(),
            GeocodedAddressSchema
        );
    }

    /*
        public $id;

    public array $geom;

    public string $country;
    public string $province;
    public string $district;
    public string $community;
    public string $city;
    public string $quarter;
    public string $pcode;
    public string $street;
    public string $house;
    public string $streetForSorting;
    public string $houseAsNumber;
    public ?array $entryPoints;
     */
    public static async createOrGetAddress(
        api: Api,
        geocodedAddress: GeocodedAddressType['geocoded_address']
    ): Promise<AddressResponseType> {
        return api.getData<any, AddressResponseType>(
            'post',
            '/address/create_or_get_from_geocoded_data',
            {
                house_geoId: geocodedAddress.house_geoId,
                x: geocodedAddress.x,
                y: geocodedAddress.y,
                country: geocodedAddress.countryCodes.iso2,
                province: geocodedAddress.province,
                district: geocodedAddress.district,
                community: geocodedAddress.community,
                city: geocodedAddress.city,
                quarter: geocodedAddress.quarter,
                pcode: geocodedAddress.postcode,
                street: geocodedAddress.street,
                house: geocodedAddress.house,
            },
            z.any(),
            z.object({
                id: z.number(),
            })
        );
    }
}
